import { AUTOMATON_SECTION_FEATURE_TYPES } from '@utils/map/map.constants';
import { Feature } from 'geojson';
import { GeoJSONFeature } from 'mapbox-gl';

import { Target } from '@/types/c2/c2.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import { FeatureTypeEnum, SensorUniqueKeys } from '@/types/map.types';
import {
  AutomatonSectionInfoData,
  FlightPlanInfoData,
  HistoryPopupData,
  InfoDataTypeEnum,
  PerimeterCameraInfoData,
  PolygonInfoData,
  PopupDataTypeValues,
  PopupType,
  PopupTypeEnum,
  SensorInfoData,
  StrobeInfoData,
  TargetInfoData,
} from '@/types/popup.types';
import { AbstractLocation } from '@/types/sensor/sensor.types';
import { AutomatonSectionUniqueKeys, SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';
import { ClassicFlightPlan } from '@/types/utm/flightPlan.types';

export function getPopupDataType(popup: PopupType): PopupDataTypeValues | null {
  const data = popup.data;
  return data && typeof data === 'object' && 'type' in data ? data.type : null;
}

export function haveSamePopupType(popup1: PopupType, popup2: PopupType): boolean {
  const dataType1 = getPopupDataType(popup1);
  const dataType2 = getPopupDataType(popup2);
  return popup1.type === popup2.type && dataType1 === dataType2;
}

export function isSamePopupType(
  popup: PopupType,
  type: PopupTypeEnum,
  dataType: PopupDataTypeValues | null = null,
): boolean {
  const popupDataType = getPopupDataType(popup);
  return popup.type === type && popupDataType === dataType;
}

export function findPopupByType(
  popups: PopupType[],
  type: PopupTypeEnum,
  dataType: PopupDataTypeValues | null = null,
): PopupType | null {
  return (
    popups.find((popup) => {
      return isSamePopupType(popup, type, dataType);
    }) ?? null
  );
}

export function isOpenedPopupByType(
  popups: PopupType[],
  type: PopupTypeEnum,
  dataType: PopupDataTypeValues | null = null,
): boolean {
  return !!findPopupByType(popups, type, dataType);
}

export function checkIfPopupIsFromFeatureValue(popup: PopupType | null, featureValue: unknown): boolean {
  if (!popup) {
    return false;
  }
  if ([PopupTypeEnum.INFO, PopupTypeEnum.HISTORY].includes(popup.type)) {
    const dataType = getPopupDataType(popup);

    switch (dataType) {
      case InfoDataTypeEnum.INFO_FLIGHT_PLAN:
        return (popup.data as FlightPlanInfoData).featureValue.code === (featureValue as ClassicFlightPlan).code;

      case InfoDataTypeEnum.INFO_PERIMETER_CAMERA:
        return (
          (popup.data as PerimeterCameraInfoData).featureValue.uniqueKey === (featureValue as PerimeterCamera).uniqueKey
        );

      case InfoDataTypeEnum.INFO_AUTOMATON_SECTION: {
        const sectionNames = (popup.data as AutomatonSectionInfoData).featureValue;
        const featureSectionNames = featureValue as AutomatonSectionUniqueKeys;

        return (
          sectionNames.sectionName === featureSectionNames.sectionName &&
          sectionNames.cabinetUniqueKey === featureSectionNames.cabinetUniqueKey &&
          sectionNames.automatonUniqueKey === featureSectionNames.automatonUniqueKey
        );
      }

      case InfoDataTypeEnum.INFO_SENSOR: {
        const uniqueKeys = (popup.data as SensorInfoData).featureValue;
        const featureUniqueKeys = featureValue as SensorUniqueKeys;

        return (
          uniqueKeys.sensorUniqueKey === featureUniqueKeys.sensorUniqueKey &&
          uniqueKeys.parentUniqueKey === featureUniqueKeys.parentUniqueKey
        );
      }

      case NotificationTypeEnum.SENSOR: {
        const uniqueKey = (popup.data as HistoryPopupData).sensorUniqueKeys?.sensorUniqueKey;
        const featureUniqueKeys = featureValue as SensorUniqueKeys;

        return uniqueKey === featureUniqueKeys.sensorUniqueKey;
      }

      case InfoDataTypeEnum.INFO_POLYGON:
      case InfoDataTypeEnum.INFO_STROBE:
      case InfoDataTypeEnum.INFO_TARGET:
        return (
          (popup.data as PolygonInfoData | StrobeInfoData | TargetInfoData).targetId ===
          (featureValue as Target<AbstractLocation>).id
        );

      default:
        return false;
    }
  }
  if (popup.type === PopupTypeEnum.ROUND) {
    const uniqueKey = popup.data.featureValue;
    const featureUniqueKeys = featureValue as SensorUniqueKeys;

    return uniqueKey === featureUniqueKeys.sensorUniqueKey;
  }

  return false;
}

export function checkIfPopupIsFromFeature(popup: PopupType, feature: GeoJSONFeature): boolean {
  if (!feature.properties) {
    return false;
  }
  const featureValue = JSON.parse(feature.properties.value);

  return checkIfPopupIsFromFeatureValue(popup, featureValue);
}

const popupTypeToMenuKey: Partial<Record<PopupTypeEnum, string>> = {
  [PopupTypeEnum.MEASURE_INFO]: 'azimuthAndDistance',
  [PopupTypeEnum.POI]: 'createPoi',
  [PopupTypeEnum.ZONE]: 'create',
  [PopupTypeEnum.MAP_LAYER]: 'createMapLayer',
  [PopupTypeEnum.FILTERS_MENU]: 'filters',
  [PopupTypeEnum.DAILY_FLIGHT_PLAN]: 'dailyFlightPlans',
  [PopupTypeEnum.NEUTRAL_INFO]: 'neutralInfo',
};

export function getNewMenuNavigationByClosedPopupType(type: PopupTypeEnum, menuNavigation: string[]) {
  const keyToRemove = popupTypeToMenuKey[type];

  if (!keyToRemove) {
    return menuNavigation;
  }

  return menuNavigation.filter((item) => item !== keyToRemove);
}

export const isTargetOrPeriSensorFeature = (feature: Feature): boolean => {
  if (!feature.properties?.featureType) {
    return false;
  }

  const featureType = feature.properties.featureType;
  if ([...AUTOMATON_SECTION_FEATURE_TYPES, FeatureTypeEnum.TARGET_ICON].includes(featureType)) {
    return true;
  }

  if (![FeatureTypeEnum.SENSOR_ICON, FeatureTypeEnum.SENSOR_LABEL].includes(featureType)) {
    return false;
  }
  const sensorType = feature.properties.type;
  return [SensorTypeEnum.AUTOMATON, SubSensorTypeEnum.AUTOMATON_CABINET].includes(sensorType);
};
